import PropTypes from 'prop-types';
import styled from 'styled-components';

import { config } from '_config';
import { theme } from '_constants/theme';

import * as Layout from '_components/atomic/layouts';
import JoinBanner from '../JoinBanner';

export const registeringYourTrademarkGuideContents = [
  'What is a Trademark?',
  'Why Register a Trademark?',
  'Steps to Register Your Trademark',
];

const StyledContainer = styled.div`
  position: relative;
`;

const StyledBlock = styled.div`
  position: relative;
  z-index: 1;

  ul,
  ol {
    padding-top: ${theme.spaces.regular}px;

    li {
      margin-bottom: ${theme.spaces.regular}px;

      b {
        text-decoration: underline;
      }
    }
  }
`;

const StyledBackgroundImageContainer = styled.div`
  position: absolute;
  top: 5%;
  width: 100%;

  img {
    width: 100%;
  }
`;

const RegisteringYourTrademarkGuideArticle = ({ idPrefix }) => {
  return (
    <StyledContainer>
      <Layout.Card.Text>
        In today&apos;s competitive business landscape, protecting your brand is
        crucial. One of the most effective ways to safeguard your brand identity
        is by registering your trademark. This guide will walk you through the
        essential steps and considerations for successfully registering your
        trademark.
      </Layout.Card.Text>

      <StyledBlock id={`${idPrefix}-${0}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          What is a Trademark?
        </Layout.Card.Text>
        <Layout.Card.Text>
          A trademark is a symbol, word, phrase, or design that identifies and
          distinguishes the source of the goods or services of one party from
          those of others. It serves as a valuable asset, providing legal
          protection and enhancing brand recognition.
        </Layout.Card.Text>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${1}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          Why Register a Trademark?
        </Layout.Card.Text>
        <Layout.Card.Text>
          <ol>
            <li>
              <b>Legal Protection:</b> Registration grants you exclusive rights
              to use the trademark nationwide and provides legal recourse in
              case of infringement.
            </li>
            <li>
              <b>Brand Recognition:</b> A registered trademark helps establish
              and maintain brand identity, fostering consumer trust and loyalty.
            </li>
            <li>
              <b>Asset Value:</b> A trademark is an intangible asset that can
              appreciate in value, making your business more attractive to
              investors or buyers.
            </li>
            <li>
              <b>Deterrent to Infringement:</b> Registered trademarks deter
              others from using similar marks, reducing the risk of brand
              dilution.
            </li>
          </ol>
        </Layout.Card.Text>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${2}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          Steps to Register Your Trademark
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          1. Conduct a Trademark Search
        </Layout.Card.Text>
        <Layout.Card.Text>
          Before applying, conduct a thorough trademark search to ensure that
          your desired mark is not already in use. Utilize online databases like
          the United States Patent and Trademark Office (USPTO) database and
          consult with a trademark attorney to avoid potential conflicts.
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          2. Choose a Strong Mark
        </Layout.Card.Text>
        <Layout.Card.Text>
          Select a mark that is distinctive and not generic or descriptive.
          Strong trademarks are usually arbitrary, fanciful, or suggestive. For
          example, Apple for computers is an arbitrary mark, while Google is a
          fanciful mark.
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          3. Prepare Your Application
        </Layout.Card.Text>
        <Layout.Card.Text>
          Gather the necessary information and documents for your application.
          This includes:
          <ul>
            <li>The name and address of the trademark owner.</li>
            <li>A clear depiction of the trademark.</li>
            <li>
              A description of the goods or services associated with the
              trademark.
            </li>
            <li>
              The date of first use of the trademark in commerce (if
              applicable).
            </li>
          </ul>
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          4. File the Application
        </Layout.Card.Text>
        <Layout.Card.Text>
          Submit your trademark application online through the USPTO&apos;s
          Trademark Electronic Application System (TEAS). There are several
          application forms available, including TEAS Plus and TEAS Standard,
          each with different requirements and fees.
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          5. Respond to Office Actions
        </Layout.Card.Text>
        <Layout.Card.Text>
          The USPTO will review your application and may issue an office action
          requiring additional information or clarification. Respond promptly
          and accurately to any requests to avoid delays in the registration
          process.
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          6. Publication and Opposition
        </Layout.Card.Text>
        <Layout.Card.Text>
          If your application meets all requirements, your trademark will be
          published in the USPTO&apos;s Official Gazette. This allows others to
          oppose the registration if they believe it will harm their existing
          trademark rights. If no opposition is filed, your trademark will
          proceed to registration.
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          7. Receive Your Registration Certificate
        </Layout.Card.Text>
        <Layout.Card.Text>
          Once your trademark is approved, you will receive a registration
          certificate from the USPTO. This certificate confirms your exclusive
          rights to use the trademark in connection with the specified goods or
          services.
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          Maintaining Your Trademark
        </Layout.Card.Text>
        <Layout.Card.Text>
          Trademark registration is not a one-time process. To maintain your
          trademark, you must:
          <ul>
            <li>Use the trademark consistently in commerce.</li>
            <li>
              File maintenance documents, such as the Declaration of Use, at
              regular intervals.
            </li>
            <li>Renew your trademark registration every ten years.</li>
          </ul>
        </Layout.Card.Text>
      </StyledBlock>
      <Layout.Card.Text>
        Registering your trademark is a critical step in protecting your brand
        and ensuring its long-term success. By following these steps and seeking
        professional guidance, you can secure the exclusive rights to your
        trademark and enhance your brand&apos;s value and recognition
      </Layout.Card.Text>
      <Layout.Card.Text>
        For more information and assistance with trademark registration, consult
        a qualified trademark attorney or visit the USPTO website.
      </Layout.Card.Text>
      <Layout.Card.Text>
        By following this comprehensive guide, you&apos;ll be well-equipped to
        navigate the trademark registration process and safeguard your
        brand&apos;s identity. Registering your trademark is an investment in
        your business&apos;s future, providing legal protection, enhancing brand
        recognition, and adding value to your enterprise.
      </Layout.Card.Text>
      <JoinBanner />
      <StyledBackgroundImageContainer>
        <picture>
          <source
            srcSet={`${config.basePath}/images/blog-arrow-logo2.svg`}
            type="image/svg+xml"
          />
          <img
            src={`${config.basePath}/images/blog-arrow-logo2.svg`}
            alt="Background Logo"
          />
        </picture>
      </StyledBackgroundImageContainer>
    </StyledContainer>
  );
};

RegisteringYourTrademarkGuideArticle.propTypes = {
  idPrefix: PropTypes.string,
};

export default RegisteringYourTrademarkGuideArticle;
